/**
 * Entity list view — /m/$moduleKey/$entityKey
 *
 * Loads the entity definition + a page of records and renders them via
 * <EntityList>. Search, pagination, and sort state live here so a
 * navigation into detail and back preserves filters (via URL params
 * next — for MVP we just keep them in local state).
 */

import type { ComputedFieldDefinition, FieldDefinition } from '@cloud/custom-modules'
import type { CalendarViewConfig, KanbanViewConfig, ViewDefinition } from '@cloud/modules'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useTranslations } from 'gt-react'
import { toast } from 'sonner'
import { z } from 'zod'
import { CsvImportExport } from '@/components/custom/csv-import-export'
import { EntityList } from '@/components/custom/entity-list'
import { CalendarView } from '@/components/custom/views/calendar-view'
import { KanbanView } from '@/components/custom/views/kanban-view'
import { ViewSwitcher } from '@/components/custom/views/view-switcher'
import { PageHeader } from '@/components/layout/page-header'
import { Skeleton } from '@/components/ui/skeleton'
import { errorMessage } from '@/lib/error-message'
import { orpc } from '@/lib/orpcClient'

/**
 * Task #25: list view state lives in URL search params so users can
 * share filtered/sorted/paginated links and the back button restores
 * the exact view they were on. The schema is loose — every field is
 * optional and has a sensible default — so old links and links with
 * missing keys still render.
 */
const listSearchSchema = z.object({
  q: z.string().optional(),
  page: z.coerce.number().int().min(1).optional(),
  sortBy: z.string().optional(),
  sortDir: z.enum(['asc', 'desc']).optional(),
  view: z.string().optional(),
})

export const Route = createFileRoute('/m/$moduleKey/$entityKey/')({
  component: EntityListPage,
  validateSearch: listSearchSchema,
})

function EntityListPage() {
  const { moduleKey, entityKey } = Route.useParams()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: '/m/$moduleKey/$entityKey/' })
  const queryClient = useQueryClient()
  const t = useTranslations()

  // Bind URL params to local-feeling getters/setters. Each setter
  // patches the URL via TanStack Router's navigate({search}) which
  // pushes a new history entry — so back/forward navigates between
  // filter states naturally.
  const searchTerm = search.q ?? ''
  const page = search.page ?? 1
  const sortBy = search.sortBy ?? null
  const sortDir = search.sortDir ?? 'asc'
  const currentViewKey = search.view ?? '__default__'

  const setSearch = (q: string) => {
    void navigate({ search: (prev) => ({ ...prev, q: q || undefined, page: 1 }) })
  }
  const setPage = (p: number) => {
    void navigate({ search: (prev) => ({ ...prev, page: p === 1 ? undefined : p }) })
  }
  const setSort = (key: string | null, dir: 'asc' | 'desc') => {
    void navigate({
      search: (prev) => ({
        ...prev,
        sortBy: key ?? undefined,
        sortDir: dir === 'asc' ? undefined : dir,
        page: 1,
      }),
    })
  }
  const setCurrentViewKey = (v: string) => {
    void navigate({
      search: (prev) => ({ ...prev, view: v === '__default__' ? undefined : v }),
    })
  }

  const { data: views = [] } = useQuery(
    orpc.extensions.views.list.queryOptions({ input: { moduleKey, entityKey } }),
  )
  const currentView =
    currentViewKey === '__default__'
      ? null
      : (views.find((v) => v.viewKey === currentViewKey) ?? null)

  const { data: module } = useQuery(
    orpc.modules.custom.modules.get.queryOptions({ input: { key: moduleKey } }),
  )

  const { data: entity, isLoading: entityLoading } = useQuery(
    orpc.modules.custom.entities.get.queryOptions({
      input: { moduleKey, key: entityKey },
    }),
  )

  const { data: recordsData, isLoading: recordsLoading } = useQuery(
    orpc.modules.custom.records.list.queryOptions({
      input: {
        moduleKey,
        entityKey,
        page,
        pageSize: 50,
        search: searchTerm || undefined,
        sortBy: sortBy ?? undefined,
        sortDir,
      },
    }),
  )

  const deleteMutation = useMutation(
    orpc.modules.custom.records.delete.mutationOptions({
      onSuccess: () => {
        toast.success(t('entityRuntime.actions.delete'))
        void queryClient.invalidateQueries({
          queryKey: orpc.modules.custom.records.list.queryKey({
            input: { moduleKey, entityKey },
          }),
        })
      },
      onError: (err) => toast.error(errorMessage(err, t)),
    }),
  )

  if (entityLoading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-10 w-full max-w-sm" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (!entity) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold">Entity not found</h1>
        <p className="text-muted-foreground mt-2">
          Entity '{entityKey}' doesn't exist in module '{moduleKey}'.
        </p>
      </div>
    )
  }

  const pagination = recordsData?.pagination
  const entityLabelPlural = entity.namePlural ?? entity.name

  return (
    <div className="p-6 space-y-4">
      <PageHeader
        breadcrumbs={[
          { label: module?.name ?? moduleKey, to: '/m/$moduleKey', params: { moduleKey } },
          { label: entityLabelPlural },
        ]}
        title={entityLabelPlural}
        subtitle={
          pagination
            ? `${pagination.total} ${pagination.total === 1 ? entity.name : entityLabelPlural}`
            : undefined
        }
        actions={
          <>
            <CsvImportExport
              moduleKey={moduleKey}
              entityKey={entityKey}
              fields={entity.fields as FieldDefinition[]}
            />
            <ViewSwitcher
              views={views as unknown as ViewDefinition[]}
              currentKey={currentViewKey}
              onChange={setCurrentViewKey}
              newViewHref={{
                to: '/settings/extensions/$moduleKey/$entityKey/views',
                params: { moduleKey, entityKey },
              }}
            />
          </>
        }
      />

      {currentView ? (
        <ViewContent
          view={currentView as unknown as ViewDefinition}
          records={recordsData?.records ?? []}
          fields={entity.fields as FieldDefinition[]}
          moduleKey={moduleKey}
          entityKey={entityKey}
          queryClient={queryClient}
        />
      ) : (
        <EntityList
          routeBase={{
            detail: '/m/$moduleKey/$entityKey/$recordId',
            new: '/m/$moduleKey/$entityKey/new',
          }}
          routeParams={{ moduleKey, entityKey }}
          entityName={entity.name}
          entityNamePlural={entity.namePlural ?? undefined}
          fields={entity.fields as FieldDefinition[]}
          computedFields={entity.computedFields as ComputedFieldDefinition[]}
          records={recordsData?.records ?? []}
          pagination={
            pagination ?? {
              page: 1,
              pageSize: 50,
              total: 0,
              totalPages: 1,
            }
          }
          search={searchTerm}
          sortBy={sortBy}
          sortDir={sortDir}
          isLoading={recordsLoading}
          onSearchChange={setSearch}
          onPageChange={setPage}
          onSortChange={setSort}
          onDelete={(recordId) => deleteMutation.mutate({ moduleKey, entityKey, recordId })}
        />
      )}
    </div>
  )
}

/**
 * Inner component that dispatches on the current view's type. Kept
 * separate so the main EntityListPage doesn't balloon — each new
 * view type gets a new branch here without touching the top-level.
 */
function ViewContent({
  view,
  records,
  fields,
  moduleKey,
  entityKey,
  queryClient,
}: {
  view: ViewDefinition
  records: Array<{ id: string; data: Record<string, unknown> }>
  fields: FieldDefinition[]
  moduleKey: string
  entityKey: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  queryClient: any
}) {
  const t = useTranslations()
  const navigate = useNavigate()
  // Mutation for Kanban drag-to-change-status: patches the group-by
  // field on the record and invalidates the list query.
  const updateMutation = useMutation(
    orpc.modules.custom.records.update.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: orpc.modules.custom.records.list.queryKey({
            input: { moduleKey, entityKey, page: 1, pageSize: 50 },
          }),
        })
      },
      onError: (err: { message: string }) => toast.error(errorMessage(err, t)),
    }),
  )

  if (view.type === 'kanban') {
    const config = view.config as unknown as KanbanViewConfig
    return (
      <KanbanView
        records={records}
        fields={fields}
        config={config}
        onMoveCard={(recordId, newGroupValue) => {
          updateMutation.mutate({
            moduleKey,
            entityKey,
            recordId: String(recordId),
            data: {
              [config.groupByField]: newGroupValue === '__none__' ? null : newGroupValue,
            },
          })
        }}
      />
    )
  }

  if (view.type === 'calendar') {
    const config = view.config as unknown as CalendarViewConfig
    return (
      <CalendarView
        records={records}
        fields={fields}
        config={config}
        onRecordClick={(recordId) => {
          void navigate({
            to: '/m/$moduleKey/$entityKey/$recordId',
            params: { moduleKey, entityKey, recordId: String(recordId) },
          })
        }}
      />
    )
  }

  return (
    <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
      <p>
        View type <code className="font-mono">{view.type}</code> isn't implemented yet.
      </p>
      <p className="mt-1 text-xs">
        The renderer component lands when the view type graduates from "coming soon".
      </p>
    </div>
  )
}
